/*
 * Copy generated from an extraction.
 *
 * The approved design's banner reads:
 *
 *   "AI read the image: clustered-column pre-selected · 5 data rows, legend &
 *    colors extracted. Review, then create."
 *
 * — which is a *report*, not a caption: every clause names something the model
 * either did or did not recover. Building it from the response rather than
 * shipping the mockup's literal string is the difference between the banner
 * being true and it being decoration; a chart with no legend and no sampled
 * colours must not claim both were extracted.
 *
 * Lives beside the sheet rather than in services/ because it is presentation:
 * the only thing it produces is a sentence for one component.
 */

import type { ChartInsertSummary } from "../services/chartSlideInsert";
import type { ConvertChartResult, EditableChart } from "../services/convertChartApi";

/** True when the extraction sampled a colour for at least one series/slice. */
function hasExtractedColors(chart: EditableChart): boolean {
  if (chart.sliceColors && chart.sliceColors.length > 0) return true;
  return chart.series.some((series) => series.color !== null);
}

/** "5 data rows" / "1 data row" — the count of categories, which is what a
 *  reader of the source chart counts as its rows. */
function dataRowsPhrase(count: number): string {
  return `${count} data ${count === 1 ? "row" : "rows"}`;
}

/** The "legend & colors extracted" clause, naming only what was actually
 *  recovered — and omitted entirely when neither was. */
function extractedPhrase(chart: EditableChart): string {
  const parts: string[] = [];
  if (chart.showLegend) parts.push("legend");
  if (hasExtractedColors(chart)) parts.push("colors");
  if (parts.length === 0) return "";
  return `, ${parts.join(" & ")} extracted`;
}

/**
 * The sheet's banner line.
 *
 * @param typeLabel The pre-selected tile's caption, lowercased into the
 *   sentence so it reads as prose ("column pre-selected") rather than as a
 *   button name quoted mid-sentence.
 */
export function chartSummaryLine(result: ConvertChartResult, typeLabel: string): string {
  const { chart } = result;
  const rows = dataRowsPhrase(chart.categories.length);
  return `AI read the image: ${typeLabel.toLowerCase()} pre-selected · ${rows}${extractedPhrase(chart)}. Review, then create.`;
}

/**
 * The toast shown once the chart is on the slide.
 *
 * The slide number is dropped when it couldn't be read rather than guessed —
 * "added on a new slide" is still true, and naming the wrong slide would send
 * the user to the wrong place.
 */
export function insertedChartMessage(summary: ChartInsertSummary): string {
  const where = summary.slideIndex === null ? "on a new slide" : `on new slide ${summary.slideIndex}`;
  // "series" is its own plural, so only the row count needs agreement.
  return `Chart added ${where} — ${summary.seriesCount} series across ${dataRowsPhrase(summary.categoryCount)}.`;
}
